// Tính "Tổng số tiền ghi nợ" phía FE — dùng chung cho container (gửi lên API
// makerInitInternationalTransfer) và StepFour (hiển thị), đảm bảo nhất quán.

const ZERO_DECIMAL_CCY = [
  "VND", "JPY", "KRW", "XOF", "XAF", "XPF", "CLP", "PYG",
  "BIF", "DJF", "GNF", "KMF", "RWF", "UGX", "VUV",
];
const THREE_DECIMAL_CCY = ["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"];

export const getCurrencyScale = (code?: string): number => {
  const c = (code || "").toUpperCase();
  if (ZERO_DECIMAL_CCY.includes(c)) return 0;
  if (THREE_DECIMAL_CCY.includes(c)) return 3;
  return 2;
};

export const formatMoney = (value: number, scale: number) =>
  (value || 0).toLocaleString("en-US", {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  });

export const toNumAmount = (v?: string) =>
  Number(String(v || "").replace(/[^\d.]/g, "")) || 0;

const roundScale = (v: number, s: number) => {
  const f = 10 ** s;
  return Math.round(v * f) / f;
};

const FEE_RATE = 0.001;
const FEE_MIN = 5;

export interface IntlDebitParams {
  amount?: string; // số tiền giao dịch (chuỗi từ form)
  currency: string; // loại tiền giao dịch
  feeType?: string; // OUR / SHA / BEN
  foreignAmount?: string; // số tiền mua NT (nếu có)
  needBuyCurrency?: string; // "Y" / "N"
  accountCcy?: string; // loại tiền của tài khoản nguồn
  currencies?: { code: string; vndRate?: string | number }[];
}

export interface IntlDebitResult {
  value: number; // số tiền ghi nợ (theo loại tiền TK nguồn)
  scale: number;
  currency: string; // loại tiền TK nguồn
  display: string; // "value CCY"
}

// Tổng số tiền ghi nợ: tiền chuyển + phí (KHÔNG cộng tiền mua ngoại tệ), quy về
// loại tiền của TK nguồn.
export const computeIntlDebit = ({
  amount,
  currency,
  feeType,
  accountCcy: accountCcyInput,
  currencies = [],
}: IntlDebitParams): IntlDebitResult => {
  const amt = toNumAmount(amount);
  const currencyScale = getCurrencyScale(currency);
  const rate = Number(currencies.find((c) => c.code === currency)?.vndRate) || 0;
  const feeForeign =
    amt > 0 ? roundScale(Math.max(amt * FEE_RATE, FEE_MIN), currencyScale) : 0;
  const type = feeType || "OUR";
  let totalForeign = amt;
  if (type === "OUR") totalForeign = amt + feeForeign;
  else if (type === "SHA") totalForeign = amt + feeForeign / 2;
  const debitBase = totalForeign;
  const accountCcy = accountCcyInput || "VND";
  const accountRate =
    accountCcy === "VND"
      ? 1
      : Number(currencies.find((c) => c.code === accountCcy)?.vndRate) || 1;
  const debitScale = accountCcy === "VND" ? 0 : getCurrencyScale(accountCcy);
  const value =
    accountCcy === currency
      ? debitBase
      : roundScale((debitBase * rate) / accountRate, debitScale);
  return {
    value,
    scale: debitScale,
    currency: accountCcy,
    display: `${formatMoney(value, debitScale)} ${accountCcy}`,
  };
};
